import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertCircle,
  FiSave,
  FiSearch,
  FiSend,
  FiTrash2,
  FiUpload,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import AgreementField from './AgreementField';
import { useAuth } from "../../context/AuthContext";
import PurchaseOrderProductFilterModal from "./components/PurchaseOrderProductFilterModal";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const resolveGstRate = (bands, discountedUnitPrice) => {
  const value = Number(discountedUnitPrice || 0);

  const matchingBand = (bands || []).find((band) => {
    const minimum =
      band.minimumItemValue === null || band.minimumItemValue === undefined
        ? null
        : Number(band.minimumItemValue);
    const maximum =
      band.maximumItemValue === null || band.maximumItemValue === undefined
        ? null
        : Number(band.maximumItemValue);

    return (minimum === null || value >= minimum) &&
      (maximum === null || value <= maximum);
  });

  return Number(matchingBand?.rate ?? 0);
};

const itemTaxRate = (item) =>
  resolveGstRate(
    item.gstBands,
    Number(item.rate || 0) * (1 - Number(item.discount || 0) / 100),
  );

export default function CreatePurchaseOrderPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const ptFileInput = useRef(null);
  const ptSaveProgress = useRef({});
  const saveLock = useRef(false);

  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [ptGroups, setPtGroups] = useState([]);
  const [items, setItems] = useState([]);

  const [supplierId, setSupplierId] = useState("");
  const [agreementType, setAgreementType] = useState('');
  const [storeId, setStoreId] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [supplierReference, setSupplierReference] = useState("");
  const [freightAmount, setFreightAmount] = useState("");
  const [freightTaxRate, setFreightTaxRate] = useState("0");
  const [notes, setNotes] = useState("");

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [ptImporting, setPtImporting] = useState(false);
  const [ptSheetChoice, setPtSheetChoice] = useState(null);
  const [ptSelectedSheets, setPtSelectedSheets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadFormData = async () => {
      try {
        setIsLoading(true);
        setError("");

        const [supplierData, storeData, seasonData] = await Promise.all([
          apiRequest("/suppliers", { accessToken }),
          apiRequest("/inventory/stores", { accessToken }),
          apiRequest("/seasons?activeOnly=true", { accessToken }),
        ]);

        setSuppliers(Array.isArray(supplierData) ? supplierData : []);
        setStores(Array.isArray(storeData) ? storeData : []);
        setSeasons(Array.isArray(seasonData) ? seasonData : []);
      } catch (requestError) {
        setError(
          requestError.message ||
            "Could not load Purchase Order form information.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (accessToken) {
      loadFormData();
    }
  }, [accessToken]);

  const selectedSupplier = useMemo(
    () => suppliers.find((supplier) => supplier.id === supplierId),
    [suppliers, supplierId],
  );

  const calculations = useMemo(() => {
    const lines = items.map((item) => {
      const quantity = Number(item.qty || 0);
      const baseRate = Number(item.rate || 0);
      const discountPercent = Number(item.discount || 0);
      const taxRate = itemTaxRate(item);

      const taxableBeforeDiscount = quantity * baseRate;
      const discountAmount =
        taxableBeforeDiscount * (discountPercent / 100);
      const taxableAfterDiscount = taxableBeforeDiscount - discountAmount;
      const taxAmount = taxableAfterDiscount * (taxRate / 100);

      return {
        taxableBeforeDiscount,
        discountAmount,
        taxAmount,
        totalInclusive: taxableAfterDiscount + taxAmount,
      };
    });

    const subtotal = lines.reduce(
      (sum, line) => sum + line.taxableBeforeDiscount,
      0,
    );

    const discountAmount = lines.reduce(
      (sum, line) => sum + line.discountAmount,
      0,
    );

    const taxAmount = lines.reduce((sum, line) => sum + line.taxAmount, 0);

    const freightBaseAmount = Number(freightAmount || 0);
    const freightGstAmount =
      freightBaseAmount * (Number(freightTaxRate || 0) / 100);

    return {
      subtotal,
      discountAmount,
      taxAmount,
      freightBaseAmount,
      freightGstAmount,
      total:
        subtotal -
        discountAmount +
        taxAmount +
        freightBaseAmount +
        freightGstAmount,
    };
  }, [items, freightAmount, freightTaxRate]);

  const updateItem = (index, field, value) => {
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const removeItem = (index) => {
    setItems((current) =>
      current.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const addVariantsFromSearch = (selectedVariants) => {
    const existingVariantIds = new Set(
      items.map((item) => item.variantId).filter(Boolean),
    );

    const additions = selectedVariants.filter(
      (variant) => !existingVariantIds.has(variant.id),
    );

    if (!additions.length) {
      setError(
        "All selected product variants are already on this purchase order.",
      );
      return;
    }

    setItems((current) => [
      ...current,
      ...additions.map((variant) => ({
        variantId: variant.id,
        productName: variant.product?.name || "Product",
        productCode: variant.product?.productCode || "",
        variantName: variant.variantName || "",
        sku: variant.sku || "",
        supplierSku: variant.supplierSku || "",
        qty: 1,
        rate: Number(variant.purchasePrice || 0),
        gstBands: variant.gstBands || [],
        discount: 0,
      })),
    ]);

    setError("");
  };

  const changeSupplier = (nextSupplierId) => {
    setAgreementType('');
    if (supplierId && supplierId !== nextSupplierId && items.length) {
      setItems([]);
      setError(
        "Product rows were cleared because products are linked to the previously selected supplier.",
      );
    }

    setSupplierId(nextSupplierId);
  };

  const previewPtFile = async (file, sheetNames = null) => {
    setPtImporting(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("supplierId", supplierId);
      if (sheetNames) body.append("sheetNames", JSON.stringify(sheetNames));
      const response = await apiRequest("/purchases/orders/import/pt-preview", {
        method: "POST", body, accessToken,
      });
      if (response.selectionRequired) {
        setPtSheetChoice({ file, sheets: response.sheets || [] });
        setPtSelectedSheets((response.sheets || []).map((sheet) => sheet.name));
        return;
      }
      const previews = response.sheetPreviews || [];
      ptSaveProgress.current = {};
      setPtGroups(previews.map(preview => ({ key: preview.groupKey || preview.sheetName,
        sheetName: preview.sheetName, branchName: preview.branchName, seasonName: preview.seasonName,
        storeId: preview.storeId || (preview.branchName ? '' : storeId), seasonId: preview.seasonId || '',
        supplierReference: preview.supplierInvoiceNumber || '',
      })));
      setItems(previews.flatMap(preview => (preview.items || []).map(item => ({
              ptGroupKey: preview.groupKey || preview.sheetName,
              variantId: item.variantId,
              catalogDraft: item.catalogDraft,
              productName: item.product?.name || "Product",
              productCode: item.product?.productCode || "",
              variantName: item.variantName || "",
              sku: item.sku || "",
              supplierSku: item.supplierBarcode || "",
              qty: Number(item.received || 0),
              rate: 0,
              gstBands: item.gstBands || [],
              discount: 0,
            }))));
      setPtSheetChoice(null);
    } catch (requestError) {
      setError(requestError?.message || "PT workbook import failed.");
    } finally {
      setPtImporting(false);
    }
  };

  const importPtFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!supplierId) {
      setError("Select a supplier before importing a PT workbook.");
      return;
    }
    await previewPtFile(file);
  };

  const createPurchaseOrder = async () => {
    if (saveLock.current) return;
    setError("");

    if (!supplierId) {
      setError("Please select a supplier.");
      return;
    }

    if (!storeId && !ptGroups.length) {
      setError("Please select a destination store or warehouse.");
      return;
    }

    if (!items.length) {
      setError("Add at least one product variant to this purchase order.");
      return;
    }

    const invalidItem = items.find(
      (item) =>
        (!item.variantId && !item.catalogDraft) || Number(item.qty) <= 0,
    );

    if (invalidItem) {
      setError(
        "Every item needs a quantity greater than zero.",
      );
      return;
    }

    try {
      if (ptGroups.some(group => !group.storeId || !group.seasonId)) throw new Error('Select a receiving store and season for every imported group.');
      saveLock.current = true;
      setIsSaving(true);

      if (ptGroups.length) {
        for (const group of ptGroups) {
          if (ptSaveProgress.current[group.key]) continue;
          const purchaseOrder = await apiRequest('/purchases/orders', { method: 'POST', accessToken, body: {
            supplierId, storeId: group.storeId, seasonId: group.seasonId,
            agreementType: group.agreementType || undefined,
            expectedDate: expectedDate || undefined,
            supplierReference: group.supplierReference || undefined,
            notes: [notes.trim(), `PT worksheet: ${group.sheetName}`, `Branch: ${group.branchName || stores.find(store => store.id === group.storeId)?.name || ''}`, `Season: ${seasons.find(season => season.id === group.seasonId)?.name || group.seasonName || ''}`].filter(Boolean).join('\n'),
            items: items.filter(item => item.ptGroupKey === group.key).map(item => ({
              variantId: item.variantId || undefined, catalogDraft: item.catalogDraft,
              quantityOrdered: Number(item.qty),
            })),
          } });
          ptSaveProgress.current[group.key] = purchaseOrder.id;
          setPtGroups(current => current.map(value => value.key === group.key ? { ...value, saved: purchaseOrder.purchaseOrderNumber || purchaseOrder.id } : value));
        }
        navigate('/admindashboard/purchases');
        return;
      }

      const purchaseOrder = await apiRequest("/purchases/orders", {
        method: "POST",
        accessToken,
        body: {
          supplierId,
          agreementType: agreementType || undefined,
          storeId,
          expectedDate: expectedDate || undefined,
          supplierReference: supplierReference.trim() || undefined,
          notes: notes.trim() || undefined,
          items: items.map((item) => ({
            variantId: item.variantId,
            quantityOrdered: Number(item.qty),
          })),
        },
      });

      navigate(`/admindashboard/purchases/${purchaseOrder.id}`);
    } catch (requestError) {
      const saved = Object.keys(ptSaveProgress.current).length;
      setError(`${requestError.message || "Could not create the Purchase Order."}${saved ? ` ${saved} POs already saved; retry skips them. Do not reload.` : ''}`);
    } finally {
      setIsSaving(false);
      saveLock.current = false;
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-800" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <p className="text-sm text-gray-500">Purchasing / Purchase Orders</p>

        <h1 className="mt-1 text-3xl font-bold">Create Purchase Order</h1>

        <p className="mt-2 text-gray-500">
          Reserve supplier-linked quantities. Actual costs, GST, lot prices and
          freight are entered only on the GRN.
        </p>
      </div>

      {error ? (
        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      ) : null}

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-5 text-lg font-semibold">Purchase Order Details</h2>

        <div className="grid gap-4 lg:grid-cols-4">
          <input
            value="Generated automatically"
            readOnly
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-500"
          />

          <input
            type="date"
            value={expectedDate}
            onChange={(event) => setExpectedDate(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          />

          <select
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">Select Store / Warehouse</option>

            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name} ({store.code}) — {store.type}
              </option>
            ))}
          </select>

          <input
            value="Draft"
            readOnly
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-500"
          />
        </div>
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-5 text-lg font-semibold">Supplier Information</h2>

        <div className="grid gap-4 lg:grid-cols-4">
          <select
            value={supplierId}
            onChange={(event) => changeSupplier(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">Select Supplier</option>

            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.displayName} ({supplier.code})
              </option>
            ))}
          </select>

          {!ptGroups.length && <AgreementField kind="orders" supplierId={supplierId} items={items} value={agreementType} onChange={setAgreementType} />}
          <input
            value={selectedSupplier?.phone || ""}
            readOnly
            placeholder="Phone Number"
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4"
          />

          <input
            value={selectedSupplier?.email || ""}
            readOnly
            placeholder="Email Address"
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4"
          />

          <input
            value={selectedSupplier?.gstin || ""}
            readOnly
            placeholder="GST Number"
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4"
          />
        </div>
      </div>

      <div className="mb-6 overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold">Products</h2>
            <p className="mt-1 text-sm text-gray-500">
              Purchase orders contain quantities only. Enter supplier cost,
              GST, selling prices and freight on the GRN.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <input ref={ptFileInput} type="file" accept=".xlsx,.xls" onChange={importPtFile} className="hidden" />
            <button type="button" disabled={!supplierId || ptImporting || isSaving || ptGroups.some(group => group.saved)} onClick={() => ptFileInput.current?.click()} className="flex h-10 items-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-4 text-primary-800 hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"><FiUpload />{ptImporting ? "Reading PT file…" : "Import PT Excel"}</button>
            <button
              type="button"
              disabled={!supplierId || Boolean(ptGroups.length)}
              onClick={() => {
              if (!supplierId) {
                setError("Select a supplier before searching for products.");
                return;
              }

              setIsProductModalOpen(true);
            }}
              className="flex h-10 items-center gap-2 rounded-xl bg-primary-700 px-4 text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
            ><FiSearch />Find Products</button>
          </div>
        </div>

        {ptGroups.length > 0 && <div className="space-y-5 p-4">{ptGroups.map(group => <section key={group.key} className="overflow-hidden rounded-xl border border-gray-200">
          <div className="bg-primary-50 p-4"><h3 className="font-bold text-primary-800">{group.sheetName} · {group.branchName || 'Selected store'} · {group.seasonName || 'Selected season'}</h3><p className="text-sm text-gray-600">Separate quantity-only PO{group.saved ? ` · Saved: ${group.saved}` : ' · Products are staged until Save'}</p>
          <div className="mt-3 flex flex-wrap gap-3"><select aria-label="Receiving store" disabled={Boolean(group.saved)} value={group.storeId} onChange={event => setPtGroups(current => current.map(value => value.key === group.key ? { ...value, storeId: event.target.value } : value))} className="rounded-lg border p-2"><option value="">Select store</option>{stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}</select>
          <select aria-label="Season" disabled={Boolean(group.saved)} value={group.seasonId} onChange={event => setPtGroups(current => current.map(value => value.key === group.key ? { ...value, seasonId: event.target.value } : value))} className="rounded-lg border p-2"><option value="">Select season</option>{seasons.map(season => <option key={season.id} value={season.id}>{season.name}</option>)}</select></div>
          <AgreementField kind="orders" supplierId={supplierId} items={items.filter(item => item.ptGroupKey === group.key)} value={group.agreementType || ''} onChange={agreementType => setPtGroups(current => current.map(value => value.key === group.key ? { ...value, agreementType } : value))} disabled={Boolean(group.saved)} />
          </div>
          <div className="max-h-[55vh] overflow-auto"><table className="w-full min-w-[850px] border-collapse text-sm"><thead className="sticky top-0 bg-primary-800 text-white"><tr>{['Article / Product','Brand','Category / Subcategory','Size group','Colour / Size','Item / Supplier code','Quantity'].map(label => <th key={label} className="border border-primary-900 p-3 text-left">{label}</th>)}</tr></thead><tbody>{items.map((item,index) => item.ptGroupKey === group.key && <tr key={`${group.key}:${item.sku}`} className="[&>td]:border [&>td]:border-gray-200 [&>td]:p-3"><td>{item.productCode}</td><td>{item.catalogDraft?.brand}</td><td>{item.catalogDraft?.category} / {item.catalogDraft?.subCategory}</td><td>{item.catalogDraft?.sizeGroup}</td><td>{item.variantName}</td><td>{item.sku}</td><td><input aria-label={`Quantity ${item.sku}`} disabled={Boolean(group.saved)} type="number" min="0.001" step="0.001" value={item.qty} onChange={event => setItems(current => current.map((value,i) => i === index ? { ...value, qty: event.target.value } : value))} className="w-24 rounded border p-2" /></td></tr>)}</tbody></table></div>
        </section>)}</div>}
        <div className={ptGroups.length ? "hidden" : "overflow-x-auto"}>
          <table className="w-full min-w-[1120px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-4 text-left">Product Variant</th>
                <th className="px-4 py-4 text-left">SKU</th>
                <th className="px-4 py-4 text-center">Qty</th>
                <th className="hidden px-4 py-4 text-right">Rate</th>
                <th className="hidden px-4 py-4 text-center">Discount</th>
                <th className="hidden px-4 py-4 text-center">GST</th>
                <th className="hidden px-4 py-4 text-right">Amount</th>
                <th className="px-4 py-4" />
              </tr>
            </thead>

            <tbody>
              {items.length ? (
                items.map((item, index) => {
                  const quantity = Number(item.qty || 0);
                  const baseRate = Number(item.rate || 0);
                  const discountPercent = Number(item.discount || 0);
                  const taxRate = itemTaxRate(item);
                  const taxableAmount =
                    quantity * baseRate * (1 - discountPercent / 100);
                  const totalInclusive =
                    taxableAmount * (1 + taxRate / 100);

                  return (
                    <tr
                      key={item.variantId || item.sku}
                      className="border-t border-gray-100"
                    >
                      <td className="px-4 py-4">
                        <p className="font-semibold text-gray-900">
                          {item.productName}
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                          {item.variantName || "Default variant"}
                          {item.productCode ? ` · ${item.productCode}` : ""}
                        </p>
                      </td>

                      <td className="px-4 py-4">
                        <p className="font-mono text-sm text-gray-800">
                          {item.sku || "—"}
                        </p>

                        {item.supplierSku ? (
                          <p className="mt-1 text-xs text-gray-500">
                            Supplier SKU: {item.supplierSku}
                          </p>
                        ) : null}
                      </td>

                      <td className="px-4 py-4">
                        <input
                          type="number"
                          min="0.001"
                          step="0.001"
                          value={item.qty}
                          onChange={(event) =>
                            updateItem(index, "qty", event.target.value)
                          }
                          className="h-11 w-full rounded-xl border border-gray-200 px-3"
                        />
                      </td>

                      <td className="hidden px-4 py-4">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.rate}
                          onChange={(event) =>
                            updateItem(index, "rate", event.target.value)
                          }
                          className="h-11 w-full rounded-xl border border-gray-200 px-3"
                        />
                      </td>

                      <td className="hidden px-4 py-4">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={item.discount}
                          onChange={(event) =>
                            updateItem(index, "discount", event.target.value)
                          }
                          className="h-11 w-full rounded-xl border border-gray-200 px-3"
                        />
                      </td>

                      <td className="hidden px-4 py-4">
                        <input
                          type="number"
                          value={taxRate.toFixed(2)}
                          readOnly
                          title="GST is taken from the product GST slab and verified by the server."
                          className="h-11 w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-50 px-3 text-gray-600"
                        />
                      </td>

                      <td className="hidden px-4 py-4 text-right font-semibold">
                        {money(totalInclusive)}
                      </td>

                      <td className="px-4 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700"
                          aria-label="Remove item"
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-14 text-center text-gray-500"
                  >
                    Select a supplier, then use Find Products to add
                    supplier-linked variants.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-3xl border border-gray-100 bg-white p-6 lg:col-span-2">
          <h2 className="mb-4 font-semibold">Notes</h2>

          <textarea
            rows="6"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Special instructions for supplier..."
            className="w-full rounded-xl border border-gray-200 p-4"
          />

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <input
              value={supplierReference}
              onChange={(event) => setSupplierReference(event.target.value)}
              placeholder="Supplier Reference / Quotation Number"
              className="h-12 rounded-xl border border-gray-200 px-4"
            />

            <input
              value="Freight is entered on the GRN"
              readOnly
              className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-500"
            />

            <input
              value="Freight GST is entered on the GRN"
              readOnly
              className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-500"
            />
          </div>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-6">
          <h2 className="mb-5 font-semibold">Order Summary</h2>

          <div className="space-y-4">
            <div className="flex justify-between">
              <span>Purchase-order amount</span>
              <span>Set on GRN</span>
            </div>

            <div className="hidden justify-between">
              <span>Discount</span>
              <span>-{money(calculations.discountAmount)}</span>
            </div>

            <div className="hidden justify-between">
              <span>GST</span>
              <span>{money(calculations.taxAmount)}</span>
            </div>

            <div className="hidden justify-between">
              <span>Freight (Excl. GST)</span>
              <span>{money(calculations.freightBaseAmount)}</span>
            </div>

            <div className="hidden justify-between">
              <span>Freight GST ({Number(freightTaxRate || 0)}%)</span>
              <span>{money(calculations.freightGstAmount)}</span>
            </div>

            <div className="flex justify-between border-t pt-4 text-lg font-bold">
              <span>PO type</span>
              <span>Quantity-only</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={createPurchaseOrder}
          disabled={isSaving}
          className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 px-6 disabled:opacity-60"
        >
          <FiSave />
          Save Draft
        </button>

        <button
          type="button"
          onClick={createPurchaseOrder}
          disabled={isSaving}
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-6 text-white disabled:opacity-60"
        >
          <FiSend />
          {isSaving ? "Creating..." : "Create Purchase Order"}
        </button>
      </div>

      <PurchaseOrderProductFilterModal
        open={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        supplierId={supplierId}
        accessToken={accessToken}
        onAddItems={addVariantsFromSearch}
      />
      <PtSheetChooser
        selection={ptSheetChoice}
        selectedSheets={ptSelectedSheets}
        setSelectedSheets={setPtSelectedSheets}
        importing={ptImporting}
        onClose={() => setPtSheetChoice(null)}
        onConfirm={() => previewPtFile(ptSheetChoice?.file, ptSelectedSheets)}
      />
    </div>
  );
}

function PtSheetChooser({ selection, selectedSheets, setSelectedSheets, importing, onClose, onConfirm }) {
  if (!selection) return null;
  const sheets = selection.sheets || [];
  const allSelected = sheets.length > 0 && selectedSheets.length === sheets.length;
  const toggle = (name) => setSelectedSheets((current) => current.includes(name) ? current.filter((value) => value !== name) : [...current, name]);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/50 p-4"><div className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl"><div className="flex items-start justify-between border-b border-gray-100 px-6 py-5"><div><h2 className="text-xl font-bold text-gray-950">Choose PT worksheets</h2><p className="mt-1 text-sm text-gray-500">Import one, several, or all sheets. Products are staged until Save. Each worksheet / branch / season group creates a separate quantity-only PO.</p></div><button type="button" onClick={onClose} disabled={importing} className="rounded-xl p-2 text-gray-500 hover:bg-gray-100"><FiX size={20} /></button></div><div className="max-h-[52vh] space-y-2 overflow-y-auto p-6"><label className="flex cursor-pointer items-center justify-between rounded-2xl border border-primary-100 bg-primary-50 px-4 py-3"><span><span className="block font-semibold text-primary-900">Import all worksheets</span><span className="text-xs text-primary-700">Select every listed sheet</span></span><input type="checkbox" checked={allSelected} onChange={() => setSelectedSheets(allSelected ? [] : sheets.map((sheet) => sheet.name))} className="h-4 w-4 accent-primary-800" /></label>{sheets.map((sheet) => <label key={sheet.name} className="flex cursor-pointer items-center justify-between rounded-2xl border border-gray-200 px-4 py-3 hover:border-primary-200"><span><span className="block font-medium text-gray-900">{sheet.name}</span><span className="text-xs text-gray-500">{sheet.rowCount} detected row{sheet.rowCount === 1 ? "" : "s"}</span></span><input type="checkbox" checked={selectedSheets.includes(sheet.name)} onChange={() => toggle(sheet.name)} className="h-4 w-4 accent-primary-800" /></label>)}</div><div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-5"><button type="button" onClick={onClose} disabled={importing} className="h-11 rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-700">Cancel</button><button type="button" onClick={onConfirm} disabled={!selectedSheets.length || importing} className="h-11 rounded-xl bg-primary-800 px-5 text-sm font-semibold text-white disabled:opacity-50">{importing ? "Preparing preview…" : "Preview selected sheets"}</button></div></div></div>;
}
